import { parseBriefing } from '@nabu/shared/briefing';

/**
 * A message's own text, as paragraphs, lists, tables and sequences rather than one block.
 *
 * Leo, 2026-09-16: *"briefings are not quite friendly to read, theyre just a block of text
 * … mine for example list a bunch of points in a single line, that makes it really hard to
 * read and would be much better as a bulleted list."* Leo, 2026-09-18: *"we don't need to
 * think of it as images, rather visual aids to learning… from tables to flow charts and
 * diagrams… Personally in my tcm objective I'm actually taking a lot of notes and making
 * tables and what not manually as I learn."* This is the rendering half of both. The other
 * half is the generation prompt, which used to forbid structure outright and is why no
 * amount of work here would have been enough on its own — see `@nabu/shared/briefing` and
 * `BRIEFING_BLOCK_SCHEMA` in `packages/ingest/src/items.ts`.
 *
 * **Nothing here interprets markup, and that is a constraint rather than an economy.** A
 * briefing is model output written from documents the *learner* supplied, so a PDF is an
 * injection surface and this is the one screen its text reaches. `parseBriefing` returns
 * strings; React puts strings in text nodes; there is no `dangerouslySetInnerHTML` and no
 * Markdown engine on the path. A briefing containing `<script>` renders as the characters
 * `<script>`, **and so does a table cell containing one** — a cell is a string in exactly
 * the sense a paragraph is, which is the property that let F-150 add a grid without adding
 * a parser. `marked` and `markdown-it` were the obvious alternative and both pass raw HTML
 * through by default, which is the whole hole, and both cost tens of kilobytes on a phone
 * for a handful of constructs. Mermaid, for the diagram half, is the same objection with a
 * bigger download.
 *
 * **Its own file so a test can render it.** `packages/web/test/chat-prose.test.ts` renders
 * these blocks with `react-dom/server` and asserts the escaping, which is a claim the
 * comment above makes and nothing checked until F-150. `Bubble.tsx` needs `useI18n` and a
 * `ChatMessage`; this needs React and a string, so it is the piece that can be rendered in
 * a plain Node test without a DOM.
 */
export function Prose({ text }: { text: string }) {
  const blocks = parseBriefing(text);
  return (
    <>
      {blocks.map((block, index) => {
        // Index keys, which is safe here and nowhere else in this file: a message's text
        // never changes, so this list is never reordered, inserted into or filtered.
        if (block.kind === 'bullets') {
          return (
            <ul className="chat-msg-list" key={index}>
              {block.items.map((item, at) => (
                <li className="chat-msg-item" key={at}>
                  {item}
                </li>
              ))}
            </ul>
          );
        }
        if (block.kind === 'steps') {
          return (
            <ol className="chat-msg-steps" key={index}>
              {block.items.map((item, at) => (
                <li className="chat-msg-item" key={at}>
                  {item}
                </li>
              ))}
            </ol>
          );
        }
        if (block.kind === 'table') {
          return <Grid block={block} key={index} />;
        }
        if (block.kind === 'heading') {
          return (
            <h3 className="chat-msg-head" key={index}>
              {block.text}
            </h3>
          );
        }
        return (
          <p className="chat-msg-text" key={index}>
            {block.text}
          </p>
        );
      })}
    </>
  );
}

/**
 * One table, inside a box that scrolls instead of widening the page.
 *
 * **Tables are the worst thing on a narrow screen and this is a phone-first product**, so
 * the wrapper is not decoration: without `overflow-x` on a container the table's own
 * intrinsic width wins, the bubble grows, the thread grows, and the whole conversation
 * scrolls sideways — one four-column table making every message on the screen wrong. That
 * is the discipline F-136 applied to the plan; it applies here for the same reason and
 * costs one `<div>`.
 *
 * `tabIndex={0}` because a box that scrolls must be reachable by something other than a
 * finger. Without it a keyboard user can see the first two columns of a comparison and has
 * no way to reach the third, which is the column the comparison was about.
 *
 * **A ragged row is rendered ragged.** `parseBriefing` does not pad rows to a common width
 * and this does not either: padding invents a cell the model did not write, into a grid
 * whose whole claim is that every cell is a claim. The short row ends early and the gap is
 * visible, which is the honest reading of what came back.
 */
function Grid({ block }: { block: { headers: string[]; rows: string[][] } }) {
  return (
    <div className="chat-msg-tablebox" tabIndex={0}>
      <table className="chat-msg-table">
        <thead>
          <tr>
            {block.headers.map((header, at) => (
              <th scope="col" key={at}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, at) => (
            <tr key={at}>
              {row.map((value, column) => (
                // The first cell of a row is its header, which is what makes the grid
                // navigable by a screen reader: a cell announced as "LU 7" alone says
                // nothing, and "Ren Mai, confluent, LU 7" is the fact the table carries.
                column === 0 ? (
                  <th scope="row" key={column}>
                    {value}
                  </th>
                ) : (
                  <td key={column}>{value}</td>
                )
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
